import React, { useState, useEffect, useMemo } from 'react';
import { getAllAuditLogs, AuditFilters } from '../services/auditService';
import { AuditLog } from '../types';
import { 
  FiClock, 
  FiUser, 
  FiEdit, 
  FiPlusCircle, 
  FiTrash2, 
  FiRefreshCw,
  FiFilter,
  FiDownload,
  FiSearch,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import toast from 'react-hot-toast';

const AdminAuditTrailPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit] = useState(50);
  
  // Filters
  const [filters, setFilters] = useState<AuditFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [tempFilters, setTempFilters] = useState<AuditFilters>({});

  const fetchAuditLogs = async (page = 1, currentFilters = filters) => {
    setIsLoading(true);
    try {
      const response = await getAllAuditLogs(currentFilters, page, limit);
      
      // Ensure we have valid data structure
      const logsData = response.data;
      const paginationData = response.pagination;
      
      if (!Array.isArray(logsData)) {
        console.warn('Audit logs data is not an array:', logsData);
        setLogs([]);
      } else {
        setLogs(logsData);
      }
      
      setCurrentPage(paginationData?.page || 1);
      setTotalPages(paginationData?.totalPages || 1);
      setTotalRecords(paginationData?.total || 0);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const handleFilterApply = () => {
    setFilters(tempFilters);
    setCurrentPage(1);
    fetchAuditLogs(1, tempFilters);
    setShowFilters(false);
  };

  const handleFilterClear = () => {
    const emptyFilters = {};
    setTempFilters(emptyFilters);
    setFilters(emptyFilters);
    setCurrentPage(1);
    fetchAuditLogs(1, emptyFilters);
    setShowFilters(false);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchAuditLogs(page);
    }
  };

  const handleRefresh = () => {
    fetchAuditLogs(currentPage);
  };

  const exportAuditLogs = () => {
    // Simple CSV export
    const headers = ['Timestamp', 'User', 'Action', 'Employee ID', 'Changes'];
    const csvContent = [
      headers.join(','),
      ...(Array.isArray(logs) ? logs.map(log => [
        new Date(log.timestamp).toISOString(),
        `"${log.userName}"`,
        log.action,
        log.employeeId,
        `"${Array.isArray(log.fieldChanges) ? log.fieldChanges.map(c => `${c.field}: ${c.oldValue} → ${c.newValue}`).join('; ') : 'N/A'}"`
      ].join(',')) : [])
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const renderIcon = (action: string) => {
    switch (action) {
      case 'create':
      case 'user_create':
        return <FiPlusCircle className="h-5 w-5 text-green-500" />;
      case 'update':
      case 'status_change':
      case 'user_status_change':
        return <FiEdit className="h-5 w-5 text-blue-500" />;
      case 'delete':
      case 'user_delete':
        return <FiTrash2 className="h-5 w-5 text-red-500" />;
      case 'login':
        return <FiUser className="h-5 w-5 text-green-600" />;
      case 'logout':
        return <FiUser className="h-5 w-5 text-yellow-600" />;
      case 'password_reset':
        return <FiRefreshCw className="h-5 w-5 text-orange-500" />;
      case 'notification_sent':
        return <FiDownload className="h-5 w-5 text-purple-500" />;
      default:
        return <FiEdit className="h-5 w-5 text-gray-500" />;
    }
  };

  const renderActionBadge = (action: string) => {
    const colorMap = {
      create: 'bg-green-100 text-green-800',
      user_create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      status_change: 'bg-yellow-100 text-yellow-800',
      user_status_change: 'bg-yellow-100 text-yellow-800',
      delete: 'bg-red-100 text-red-800',
      user_delete: 'bg-red-100 text-red-800',
      login: 'bg-emerald-100 text-emerald-800',
      logout: 'bg-amber-100 text-amber-800',
      password_reset: 'bg-orange-100 text-orange-800',
      notification_sent: 'bg-purple-100 text-purple-800',
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colorMap[action as keyof typeof colorMap] || 'bg-gray-100 text-gray-800'}`}>
        {action.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const renderChanges = (log: AuditLog) => {
    if (log.action === 'create' || log.action === 'user_create') {
      return <span className="text-sm text-gray-600">Record created</span>;
    }
    if (log.action === 'delete' || log.action === 'user_delete') {
      return <span className="text-sm text-gray-600">Record deleted</span>;
    }
    if (log.action === 'login') {
      return <span className="text-sm text-gray-600">User logged in</span>;
    }
    if (log.action === 'logout') {
      return <span className="text-sm text-gray-600">User logged out</span>;
    }
    if (log.action === 'password_reset') {
      return (
        <span className="text-sm text-gray-600">
          Password reset for {log.targetUserName || log.targetUserId || 'user'}
        </span>
      );
    }
    if (log.action === 'password_reset_initiated') {
      return (
        <span className="text-sm text-gray-600">
          Password reset initiated for {log.metadata?.email || 'user'}
        </span>
      );
    }
    if (log.action === 'password_reset_completed') {
      return (
        <span className="text-sm text-gray-600">
          Password reset completed for {log.metadata?.email || 'user'}
        </span>
      );
    }
    if (log.action === 'notification_sent') {
      return (
        <div className="text-sm text-gray-600">
          <span className="font-medium">Notification sent</span>
          {log.metadata?.template && (
            <div className="text-xs text-gray-500 mt-1">
              Template: {log.metadata.template}
            </div>
          )}
        </div>
      );
    }
    if (Array.isArray(log.fieldChanges) && log.fieldChanges.length > 0) {
      return (
        <div className="text-sm text-gray-600">
          <span className="font-medium">{log.fieldChanges.length} field(s) changed:</span>
          <ul className="mt-1 space-y-1">
            {log.fieldChanges.slice(0, 3).map((change, index) => (
              <li key={index} className="truncate">
                <span className="font-medium">{change.field}:</span>{' '}
                <span className="text-gray-500">{JSON.stringify(change.oldValue)}</span> →{' '}
                <span className="text-gray-900">{JSON.stringify(change.newValue)}</span>
              </li>
            ))}
            {log.fieldChanges.length > 3 && (
              <li className="text-gray-500">
                ... and {log.fieldChanges.length - 3} more changes
              </li>
            )}
          </ul>
        </div>
      );
    }
    return <span className="text-sm text-gray-500">No specific changes logged</span>;
  };

  const actionOptions = [
    { value: 'create', label: 'Employee Create' },
    { value: 'update', label: 'Employee Update' },
    { value: 'status_change', label: 'Status Change' },
    { value: 'delete', label: 'Employee Delete' },
    { value: 'user_create', label: 'User Create' },
    { value: 'user_delete', label: 'User Delete' },
    { value: 'user_status_change', label: 'User Status Change' },
    { value: 'login', label: 'User Login' },
    { value: 'logout', label: 'User Logout' },
    { value: 'password_reset', label: 'Password Reset' },
    { value: 'notification_sent', label: 'Notification Sent' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Audit Trail</h1>
            <p className="text-gray-600 mt-1">
              Complete system activity log - {totalRecords} total records
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FiFilter />
              <span>Filters</span>
            </button>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <FiRefreshCw className={isLoading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
            <button
              onClick={exportAuditLogs}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiDownload />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Filter Audit Logs</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Actions</label>
              <select
                multiple
                value={tempFilters.action || []}
                onChange={(e) => setTempFilters(prev => ({
                  ...prev,
                  action: Array.from(e.target.selectedOptions, option => option.value)
                }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 h-32"
              >
                {actionOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">User ID</label>
              <input
                type="text"
                value={tempFilters.userId || ''}
                onChange={(e) => setTempFilters(prev => ({
                  ...prev,
                  userId: e.target.value || undefined
                }))}
                placeholder="Enter user ID..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Employee ID</label>
              <input
                type="text"
                value={tempFilters.employeeId || ''}
                onChange={(e) => setTempFilters(prev => ({
                  ...prev,
                  employeeId: e.target.value || undefined
                }))}
                placeholder="Enter employee ID..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={tempFilters.dateRange?.start || ''}
                onChange={(e) => setTempFilters(prev => ({
                  ...prev,
                  dateRange: {
                    ...prev.dateRange,
                    start: e.target.value || undefined
                  }
                }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={tempFilters.dateRange?.end || ''}
                onChange={(e) => setTempFilters(prev => ({
                  ...prev,
                  dateRange: {
                    ...prev.dateRange,
                    end: e.target.value || undefined
                  }
                }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Terms</label>
              <input
                type="text"
                value={tempFilters.searchTerm || ''}
                onChange={(e) => setTempFilters(prev => ({
                  ...prev,
                  searchTerm: e.target.value || undefined
                }))}
                placeholder="Search users, employee IDs..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={handleFilterClear}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={handleFilterApply}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Audit Logs */}
      <div className="bg-white shadow rounded-lg">
        {isLoading ? (
          <div className="overflow-hidden" aria-busy="true">
            <span className="sr-only">Loading audit logs</span>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Timestamp', 'User', 'Action', 'Employee', 'Details'].map((label) => (
                      <th key={label} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.from({ length: 8 }).map((_, r) => (
                    <tr key={r}>
                      {Array.from({ length: 5 }).map((_, c) => (
                        <td key={c} className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`relative overflow-hidden rounded bg-primary-300 h-4 ${
                              c === 0 ? 'w-32' : c === 1 ? 'w-28' : c === 2 ? 'w-24' : c === 3 ? 'w-36' : 'w-40'
                            }`}
                            aria-hidden="true"
                          >
                            <div
                              className="absolute inset-0 -translate-x-full animate-shimmer motion-reduce:hidden"
                              style={{
                                animationDelay: `${r * 70 + c * 30}ms`,
                                background:
                                  'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 35%, rgba(230,126,34,0.35) 50%, rgba(255,255,255,0.6) 65%, transparent 100%)',
                              }}
                            />
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : !Array.isArray(logs) || logs.length === 0 ? (
          <div className="text-center py-12">
            <FiSearch className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No audit logs found matching your criteria</p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Changes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(logs) && logs.map((log, index) => (
                    <tr key={`${log.employeeId}-${log.timestamp}-${index}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FiClock className="h-4 w-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-900">
                            {new Date(log.timestamp).toLocaleDateString()}
                            <div className="text-xs text-gray-500">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FiUser className="h-4 w-4 text-gray-400 mr-2" />
                          <div className="text-sm">
                            <div className="text-gray-900 font-medium">{log.userName}</div>
                            <div className="text-gray-500 text-xs">{log.userId}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {renderIcon(log.action)}
                          {renderActionBadge(log.action)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">{log.employeeId}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          {renderChanges(log)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{(currentPage - 1) * limit + 1}</span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * limit, totalRecords)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{totalRecords}</span>{' '}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <FiChevronLeft className="h-5 w-5" />
                    </button>
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <FiChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAuditTrailPage;
